import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  InputNumber,
  Button,
  Switch,
  Select,
  Tag,
  Alert,
  Progress,
  Tabs,
  Checkbox,
  Descriptions,
} from "antd";
import {
  CloudServerOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  UserOutlined,
  SettingOutlined,
  LinkOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { formatDateTime } from '@/utils/datetime';
import Icons from '@/components/third-party/Icons';
import DataTable from '@/components/DataTable';
import { EmptyState } from '@/components/PageHeader';
import { PageInfo } from '@/components/PageChrome';
import HeaderActions from '@/components/HeaderActions';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';

const ActiveDirectoryIntegration = ({ active = true }) => {
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const formBgColor = isDark ? defaultColors.palette.gray[750] || defaultColors.palette.gray[700] : '#f9fafb';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const accent = defaultColors.primary;
  const accentBg = isDark ? defaultColors.palette.teal[900] : defaultColors.palette.teal[50];

  const [form] = Form.useForm();
  const [attrForm] = Form.useForm();
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [syncingUsers, setSyncingUsers] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [adEnabled, setAdEnabled] = useState(false);
  const [groupMappings, setGroupMappings] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);

  const defaultConfig = {
    enabled: false,
    ldap_server: "",
    ldap_port: 389,
    use_ssl: false,
    ssl_port: 636,
    base_dn: "",
    bind_dn: "",
    bind_password: "",
    user_search_base: "",
    user_filter: "(objectClass=person)",
    group_search_base: "",
    group_filter: "(objectClass=group)",
    attributes: {
      username: "sAMAccountName",
      email: "mail",
      first_name: "givenName",
      last_name: "sn",
      display_name: "displayName",
      department: "department",
    },
  };

  useEffect(() => {
    form.setFieldsValue(defaultConfig);
    attrForm.setFieldsValue({ attributes: defaultConfig.attributes });
    setAdEnabled(defaultConfig.enabled);
    loadSyncHistory();
  }, []);

  const loadSyncHistory = () => {
    setSyncHistory([
      { id: 1, sync_date: "2025-11-01 14:30:00", users_synced: 45, users_created: 3, users_updated: 42, status: "success", duration: "2.5s" },
      { id: 2, sync_date: "2025-10-31 14:30:00", users_synced: 42, users_created: 0, users_updated: 42, status: "success", duration: "1.8s" },
    ]);
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const values = await form.validateFields(["ldap_server", "ldap_port", "base_dn", "bind_dn", "bind_password"]);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setConnectionStatus({ success: true, message: "Successfully connected to Active Directory", server_info: { server: values.ldap_server, port: values.ldap_port, base_dn: values.base_dn } });
      toast.success("AD connection successful!");
    } catch (error) {
      setConnectionStatus({ success: false, message: "Failed to connect to Active Directory", error: error.message || "Connection timeout" });
      toast.error("AD connection failed!");
    } finally {
      setTestingConnection(false);
    }
  };

  const syncUsers = async () => {
    setSyncingUsers(true);
    setSyncProgress(0);
    try {
      const interval = setInterval(() => {
        setSyncProgress((prev) => { if (prev >= 100) { clearInterval(interval); return 100; } return prev + 10; });
      }, 500);
      await new Promise((resolve) => setTimeout(resolve, 5000));
      clearInterval(interval);
      setSyncProgress(100);
      toast.success("Users synced successfully from Active Directory!");
      loadSyncHistory();
    } catch (error) {
      toast.error("Failed to sync users from AD");
    } finally {
      setSyncingUsers(false);
      setTimeout(() => setSyncProgress(0), 2000);
    }
  };

  const saveConfiguration = async () => {
    try {
      await form.validateFields();
      toast.success("AD configuration saved successfully!");
    } catch (error) {
      toast.error("Failed to save configuration");
    }
  };

  const saveAttributeMapping = async () => {
    try {
      await attrForm.validateFields();
      toast.success("Attribute mapping saved successfully!");
    } catch (error) {
      toast.error("Failed to save attribute mapping");
    }
  };

  const labelSpan = (text) => (
    <span className="font-medium" style={{ color: textColor }}>
      {text}
    </span>
  );

  const syncHistoryColumns = [
    { title: "Date/Time", dataIndex: "sync_date", key: "sync_date", sortType: "date", width: 240, render: (text) => formatDateTime(text, "DD-MMM-YYYY HH:mm:ss") },
    { title: "Users Synced", dataIndex: "users_synced", key: "users_synced", sortType: "number", width: 150 },
    { title: "Created", dataIndex: "users_created", key: "users_created", width: 120, render: (count) => <Tag color="green">{count > 0 ? `+${count}` : count}</Tag> },
    { title: "Updated", dataIndex: "users_updated", key: "users_updated", width: 120, render: (count) => <Tag>{count}</Tag> },
    { title: "Duration", dataIndex: "duration", key: "duration", width: 120 },
    {
      title: "Status", dataIndex: "status", key: "status", width: 150,
      render: (status) => (
        <Tag icon={status === "success" ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={status === "success" ? "success" : "error"}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
  ];

  return (
    <div>
      {/* hosted in User Management: the AD switch goes in that page's header, only while this tab is open */}
      {active && (
        <HeaderActions>
          <span className="inline-flex items-center gap-2">
            <span style={{ color: textColor, fontSize: 13 }}>AD Authentication:</span>
            <Switch
              checked={adEnabled}
              onChange={setAdEnabled}
              checkedChildren="Enabled"
              unCheckedChildren="Disabled"
            />
            <PageInfo text="LDAP / Active Directory connection, sync schedule and group-to-role mapping." />
          </span>
        </HeaderActions>
      )}
      {adEnabled && (
        <Alert
          message="Active Directory authentication is enabled"
          description="Users can now log in using their Active Directory credentials. Local authentication is still available as fallback."
          type="success"
          showIcon
          icon={<LockOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      <Tabs defaultActiveKey="connection" size="large" tabBarStyle={{ backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f9f9f9', borderRadius: '8px', padding: '2px 8px' }}>

        {/* ── Connection Settings ── */}
        <Tabs.TabPane tab={<span><LinkOutlined /> Connection Settings</span>} key="connection">
          <Form form={form} layout="vertical" initialValues={defaultConfig}>

            {/* Server block */}
            <div
              className="rounded-xl border mb-5"
              style={{ backgroundColor: formBgColor, borderColor, borderLeftColor: accent, borderLeftWidth: 4, borderLeftStyle: 'solid', padding: '20px 24px 12px' }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 rounded-md" style={{ backgroundColor: accentBg }}>
                  <Icons.Server className="w-4 h-4" style={{ color: accent }} />
                </div>
                <span className="text-sm font-semibold" style={{ color: accent }}>Server Connection</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', columnGap: 16 }}>
                <Form.Item
                  name="ldap_server"
                  label={labelSpan('LDAP Server')}
                  rules={[{ required: true, message: "Please enter LDAP server" }]}
                >
                  <Input placeholder="ldap.company.com or IP address" />
                </Form.Item>
                <Form.Item
                  name="ldap_port"
                  label={labelSpan('Port')}
                  rules={[{ required: true, message: "Please enter port" }]}
                >
                  <InputNumber min={1} max={65535} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item
                  name="base_dn"
                  label={labelSpan('Base DN')}
                  rules={[{ required: true, message: "Please enter Base DN" }]}
                >
                  <Input placeholder="DC=company,DC=com" />
                </Form.Item>
                <Form.Item
                  name="bind_dn"
                  label={labelSpan('Bind DN (Service Account)')}
                  rules={[{ required: true, message: "Please enter Bind DN" }]}
                >
                  <Input placeholder="CN=ldap-service,OU=Service Accounts,DC=company,DC=com" />
                </Form.Item>
                <Form.Item
                  name="bind_password"
                  label={labelSpan('Bind Password')}
                  rules={[{ required: true, message: "Please enter password" }]}
                >
                  <Input.Password placeholder="Service account password" />
                </Form.Item>
                <Form.Item name="use_ssl" valuePropName="checked" label={labelSpan('Security')}>
                  <Checkbox>Use SSL/TLS (Port 636)</Checkbox>
                </Form.Item>
              </div>
              <div className="flex justify-end gap-2 mb-2">
                <Button onClick={() => form.resetFields()}>Reset</Button>
                <Button
                  icon={<SettingOutlined />}
                  onClick={saveConfiguration}
                >
                  Save
                </Button>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={testConnection}
                  loading={testingConnection}
                >
                  Test connection
                </Button>
              </div>
            </div>

            {/* Search Configuration block */}
            <div
              className="rounded-xl border mb-5"
              style={{ backgroundColor: formBgColor, borderColor, borderLeftColor: accent, borderLeftWidth: 4, borderLeftStyle: 'solid', padding: '20px 24px 12px' }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 rounded-md" style={{ backgroundColor: accentBg }}>
                  <Icons.SlidersHorizontal className="w-4 h-4" style={{ color: accent }} />
                </div>
                <span className="text-sm font-semibold" style={{ color: accent }}>Search Configuration</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 16 }}>
                <Form.Item name="user_search_base" label={labelSpan('User Search Base')}>
                  <Input placeholder="OU=Users,DC=company,DC=com" />
                </Form.Item>
                <Form.Item name="user_filter" label={labelSpan('User Filter')}>
                  <Input placeholder="(objectClass=person)" />
                </Form.Item>
                <Form.Item name="group_search_base" label={labelSpan('Group Search Base')}>
                  <Input placeholder="OU=Groups,DC=company,DC=com" />
                </Form.Item>
                <Form.Item name="group_filter" label={labelSpan('Group Filter')}>
                  <Input placeholder="(objectClass=group)" />
                </Form.Item>
              </div>
            </div>

            {connectionStatus && (
              <Alert
                message={connectionStatus.success ? "Connection Successful" : "Connection Failed"}
                description={
                  connectionStatus.success ? (
                    <Descriptions size="small" column={1}>
                      <Descriptions.Item label="Server">{connectionStatus.server_info.server}</Descriptions.Item>
                      <Descriptions.Item label="Port">{connectionStatus.server_info.port}</Descriptions.Item>
                      <Descriptions.Item label="Base DN">{connectionStatus.server_info.base_dn}</Descriptions.Item>
                    </Descriptions>
                  ) : (
                    <div>
                      <div>{connectionStatus.message}</div>
                      {connectionStatus.error && <div style={{ marginTop: 8, color: "#ff4d4f" }}>Error: {connectionStatus.error}</div>}
                    </div>
                  )
                }
                type={connectionStatus.success ? "success" : "error"}
                showIcon
                style={{ marginTop: 8 }}
                closable
              />
            )}
          </Form>
        </Tabs.TabPane>

        {/* ── User Sync ── */}
        <Tabs.TabPane tab={<span><SyncOutlined /> User Sync</span>} key="sync">
          <div
            className="rounded-xl border mb-5"
            style={{ backgroundColor: formBgColor, borderColor, borderLeftColor: accent, borderLeftWidth: 4, borderLeftStyle: 'solid', padding: '20px 24px 12px' }}
          >
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-md" style={{ backgroundColor: accentBg }}>
                  <Icons.Repeat className="w-4 h-4" style={{ color: accent }} />
                </div>
                <span className="text-sm font-semibold" style={{ color: accent }}>Sync Users from Active Directory</span>
              </div>
              <Button
                type="primary"
                icon={<SyncOutlined />}
                onClick={syncUsers}
                loading={syncingUsers}
                disabled={!connectionStatus?.success}
               
              >
                Sync now
              </Button>
            </div>

            {syncingUsers && (
              <div style={{ marginBottom: 16 }}>
                <Progress percent={syncProgress} status="active" />
                <div style={{ textAlign: "center", marginTop: 8, color: textColor }}>Syncing users from Active Directory...</div>
              </div>
            )}

            {!connectionStatus?.success && (
              <Alert
                message="Connection required — test and verify the AD connection in the 'Connection Settings' tab before syncing users."
                type="warning"
                showIcon
                style={{ marginBottom: 8, padding: '6px 12px' }}
              />
            )}
          </div>

          <DataTable
            tableKey="adSyncHistoryTable"
            searchable
            searchKeys={['status', 'sync_date']}
            columnConfig
            showCount={false}
            className="ad-sync-history-table"
            data={syncHistory}
            columns={syncHistoryColumns}
            rowKey="id"
            bordered
            scrollX="max-content"
            height={null}
            locale={{
              emptyText: (
                <EmptyState
                  title="No sync runs yet"
                  description="Run a manual sync or enable the schedule to see history here."
                />
              ),
            }}
          />
        </Tabs.TabPane>

        {/* ── Group Mapping ── */}
        <Tabs.TabPane tab={<span><TeamOutlined /> Group Mapping</span>} key="mapping">
          <div
            className="rounded-xl border mb-5"
            style={{ backgroundColor: formBgColor, borderColor, borderLeftColor: accent, borderLeftWidth: 4, borderLeftStyle: 'solid', padding: '20px 24px 12px' }}
          >
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-md" style={{ backgroundColor: accentBg }}>
                  <Icons.GitBranch className="w-4 h-4" style={{ color: accent }} />
                </div>
                <span className="text-sm font-semibold" style={{ color: accent }}>Map AD Groups to Application Roles</span>
                <PageInfo text="Map Active Directory groups to application roles. Users in mapped AD groups will automatically receive the corresponding application role." />
              </div>
              <Button type="primary" icon={<TeamOutlined />}>
                Add Mapping
              </Button>
            </div>

          </div>

          <DataTable
            columnConfig
            showCount={false}
            className="ad-group-mapping-table"
            data={groupMappings}
            columns={[
              { title: "AD Group", dataIndex: "ad_group", key: "ad_group", width: 320 },
              { title: "Application Role", dataIndex: "app_role", key: "app_role", width: 280, render: (role) => <Tag>{role}</Tag> },
              { title: "Actions", key: "actions", width: 140, align: "center", render: () => <Button danger size="small" icon={<Icons.Trash2 className="w-3.5 h-3.5" />}>Remove</Button> },
            ]}
            bordered
            scrollX="max-content"
            height={null}
          />
        </Tabs.TabPane>

        {/* ── Attribute Mapping ── */}
        <Tabs.TabPane tab={<span><UserOutlined /> Attribute Mapping</span>} key="attributes">
          <div
            className="rounded-xl border mb-5"
            style={{ backgroundColor: formBgColor, borderColor, borderLeftColor: accent, borderLeftWidth: 4, borderLeftStyle: 'solid', padding: '20px 24px 12px' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="p-1.5 rounded-md" style={{ backgroundColor: accentBg }}>
                <Icons.User className="w-4 h-4" style={{ color: accent }} />
              </div>
              <span className="text-sm font-semibold" style={{ color: accent }}>Map AD Attributes to User Fields</span>
              <PageInfo text="Configure which Active Directory attributes map to application user fields." />
            </div>

            <Form form={attrForm} layout="vertical">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', columnGap: 16 }}>
                <Form.Item name={["attributes", "username"]} label={labelSpan('Username')}>
                  <Input placeholder="sAMAccountName" />
                </Form.Item>
                <Form.Item name={["attributes", "email"]} label={labelSpan('Email')}>
                  <Input placeholder="mail" />
                </Form.Item>
                <Form.Item name={["attributes", "first_name"]} label={labelSpan('First Name')}>
                  <Input placeholder="givenName" />
                </Form.Item>
                <Form.Item name={["attributes", "last_name"]} label={labelSpan('Last Name')}>
                  <Input placeholder="sn" />
                </Form.Item>
                <Form.Item name={["attributes", "display_name"]} label={labelSpan('Display Name')}>
                  <Input placeholder="displayName" />
                </Form.Item>
                <Form.Item name={["attributes", "department"]} label={labelSpan('Department')}>
                  <Input placeholder="department" />
                </Form.Item>
              </div>
              <div className="flex justify-end mt-2">
                <Button type="primary" icon={<Icons.Check className="w-4 h-4" />} onClick={saveAttributeMapping}>
                  Save Attribute Mapping
                </Button>
              </div>
            </Form>
          </div>
        </Tabs.TabPane>

      </Tabs>
    </div>
  );
};

export default ActiveDirectoryIntegration;
